<script setup lang="ts">
import { ref, computed } from 'vue'
import { Button } from "@/shadcn/components/ui/button"
import { PlusIcon, Trash2Icon } from "@lucide/vue"

type ExtraEntry = {
  key: string
  type: 'static' | 'reference'
  value: string
  model: string
  field: string
  format: string
}

type AvailableFormats = Record<string, string>

const props = defineProps<{
  initialData: ExtraEntry[]
  inputName: string
  referenceableFields: Record<string, string[]>
  availableFormats: AvailableFormats
}>()

const modelNames = computed(() => Object.keys(props.referenceableFields))

const defaultModel = computed(() => modelNames.value[0] ?? '')

const formatOptions = computed(() => Object.keys(props.availableFormats))

const rows = ref<ExtraEntry[]>(
  (props.initialData ?? []).map(entry => ({
    key: entry.key ?? '',
    type: entry.type ?? 'static',
    value: entry.value ?? '',
    model: entry.model ?? defaultModel.value,
    field: entry.field ?? '',
    format: entry.format ?? '',
  }))
)

const jsonOutput = computed(() => {
  return JSON.stringify(
    rows.value
      .filter(row => row.key.trim() !== '')
      .map(row => {
        if (row.type === 'reference') {
          const entry: Record<string, string> = {
            key: row.key,
            type: 'reference',
            model: row.model,
            field: row.field,
          }
          if (row.format) {
            entry.format = row.format
          }
          return entry
        }
        return { key: row.key, type: 'static', value: row.value }
      })
  )
})

const fieldsForModel = (model: string): string[] => {
  return props.referenceableFields[model] ?? []
}

const addRow = () => {
  rows.value.push({ key: '', type: 'static', value: '', model: defaultModel.value, field: '', format: '' })
}

const removeRow = (index: number) => {
  rows.value.splice(index, 1)
}

const onModelChange = (row: ExtraEntry) => {
  const fields = fieldsForModel(row.model)
  if (!fields.includes(row.field as any)) {
    row.field = fields[0] ?? ''
  }
  row.format = ''
}
</script>

<template>
  <div class="form-group">
    <label class="tw:font-semibold tw:mb-2 tw:block">Extra parameters</label>

    <div
      v-for="(row, index) in rows"
      :key="index"
      class="tw:flex tw:items-start tw:gap-2 tw:mb-2"
    >
      <input
        v-model="row.key"
        type="text"
        class="form-control tw:flex-1"
        placeholder="Parameter name"
      />

      <select
        v-model="row.type"
        class="form-control tw:w-28"
      >
        <option value="static">Static</option>
        <option value="reference">Reference</option>
      </select>

      <template v-if="row.type === 'static'">
        <input
          v-model="row.value"
          type="text"
          class="form-control tw:flex-1"
          placeholder="Value"
        />
      </template>

      <template v-else>
        <select
          v-model="row.model"
          class="form-control tw:w-36"
          @change="onModelChange(row)"
        >
          <option
            v-for="model in modelNames"
            :key="model"
            :value="model"
          >
            {{ model }}
          </option>
        </select>

        <select
          v-model="row.field"
          class="form-control tw:flex-1"
        >
          <option value="" disabled>Select field</option>
          <option
            v-for="f in fieldsForModel(row.model)"
            :key="f"
            :value="f"
          >
            {{ f }}
          </option>
        </select>

        <select
          v-if="formatOptions.length > 0"
          v-model="row.format"
          class="form-control tw:w-32"
        >
          <option value="">No format</option>
          <option
            v-for="fmt in formatOptions"
            :key="fmt"
            :value="fmt"
          >
            {{ fmt }} ({{ availableFormats[fmt] }})
          </option>
        </select>
      </template>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        class="tw:shrink-0 tw:text-red-500 hover:tw:text-red-700"
        @click="removeRow(index)"
      >
        <Trash2Icon class="tw:w-4 tw:h-4" />
      </Button>
    </div>

    <Button
      type="button"
      variant="outline"
      size="sm"
      @click="addRow"
    >
      <PlusIcon class="tw:w-4 tw:h-4 tw:mr-1" />
      {{ $t('Add parameter') }}
    </Button>

    <input type="hidden" :name="inputName" :value="jsonOutput" />
  </div>
</template>
